import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import styled, { createGlobalStyle } from "styled-components";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";

const GlobalAnimations = createGlobalStyle`
  @keyframes titaniumShift {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
  }
`;

// ======================= NAV =======================
const Nav = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  padding: 1rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.35s ease;

  @media (min-width: 1024px) {
    width: ${({ scrolled }) => (scrolled ? "80%" : "90%")};
    left: 50%;
    transform: translateX(-50%);
    border-radius: ${({ scrolled }) => (scrolled ? "20px" : "0")};
    padding: ${({ scrolled }) => (scrolled ? "1rem" : "1.5rem")};
    top: ${({ scrolled }) => (scrolled ? "15px" : "20px")};
    background: ${({ scrolled }) => (scrolled ? "rgba(255, 255, 255, 0.05)" : "transparent")};
    backdrop-filter: ${({ scrolled }) => scrolled ? "blur(30px)" : "none"};
    -webkit-backdrop-filter: ${({ scrolled }) => scrolled ? "blur(30px)" : "none"};
    box-shadow: ${({ scrolled }) => scrolled ? "0 8px 30px rgba(0,0,0,0.5)" : "none"};
  }

  @media (max-width: 1023px) {
    background: rgba(10, 10, 10, 0.25);
    backdrop-filter: blur(20px);
  }
`;

const Logo = styled(Link)`
  font-size: 1.5rem;
  font-weight: 900;
  letter-spacing: -0.03em;
  text-decoration: none;
  background: linear-gradient(120deg, #7abdffff, #00eeffff, #2b99d4ff, #469cd5ff, #1255fdff, #002efcff);
  background-size: 300%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: titaniumShift 30s ease-in-out infinite;
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2rem;
  align-items: center;

  a {
    font-size: 1rem;
    text-decoration: none;
    color: #ddd;
    opacity: 0.9;
    transition: 0.2s;
    &:hover { opacity: 1; color: white; }
  }

  @media (max-width: 768px) { display: none; }
`;

const DownloadButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 0.8rem;
  font-size: 0.95rem;
  font-weight: 200;
  color: white;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.7);
  border-radius: 12px;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: white;
    box-shadow: 0 0 15px rgba(255, 255, 255, 0.2);
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 0.75rem 1rem;
    font-size: 1.1rem;
    border-radius: 14px;
  }
`;

// ======================= БУРГЕР =======================
const Burger = styled.button`
  display: none;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 1100;

  @media (max-width: 768px) {
    display: flex;
  }

  span {
    display: block;
    width: 24px;
    height: 2px;
    background: white;
    border-radius: 10px;
    transition: all 0.3s ease;

    &:nth-child(1) { transform: ${({ open }) => open ? "rotate(45deg) translate(6px, 6px)" : "none"}; }
    &:nth-child(2) { opacity: ${({ open }) => open ? 0 : 1}; margin: 6px 0; }
    &:nth-child(3) { transform: ${({ open }) => open ? "rotate(-45deg) translate(6px, -6px)" : "none"}; }
  }
`;

// ======================= МОБИЛЬНОЕ МЕНЮ — ПРЕМИУМ АНИМАЦИЯ =======================
const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 68px;
  left: 0;
  right: 0;
  background: rgba(10, 10, 20, 0.17);
  backdrop-filter: blur(30px);
  -webkit-backdrop-filter: blur(30px);
  padding: 2rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.8rem;
  z-index: 999;
  border-radius: 0 0 24px 24px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.6);
  overflow: hidden;
`;

// Анимация контейнера меню
const menuContainerVariants = {
  open: {
    y: 0,
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1],
      when: "beforeChildren",
      staggerChildren: 0.08,
      delayChildren: 0.15
    }
  },
  closed: {
    y: -30,
    opacity: 0,
    scale: 0.95,
    transition: {
      duration: 0.35,
      ease: [0.4, 0, 0.2, 1],
      when: "afterChildren"
    }
  }
};

// Анимация пунктов
const itemVariants = {
  open: { opacity: 1, y: 0, transition: { duration: 0.2 } },
  closed: { opacity: 0, y: -20 }
};

const langItemVariants = {
  open: { opacity: 1, x: 0 },
  closed: { opacity: 0, x: -20 }
};

// ======================= ЯЗЫКИ =======================
const languages = [
  { code: "en", name: "🇬🇧 English", flag: "" },
  { code: "ru", name: "🇷🇺 Русский", flag: "" },
  { code: "kk", name: "🇰🇿 Қазақша", flag: "" },
  { code: "es", name: "🇪🇸 Español", flag: "" },
  { code: "fr", name: "🇫🇷 French", flag: "" }
];

const Chevron = styled(motion.span)`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-right: 2px solid white;
  border-bottom: 2px solid white;
  transform: rotate(45deg);
  margin-left: 8px;
`;

// ======================= LANGUAGE SELECTOR =======================
const LanguageSelector = ({ isMobile, onCloseMenu }) => {
  const { i18n } = useTranslation();
  const currentLang = languages.find(l => l.code === i18n.resolvedLanguage) || languages[0];

  const [desktopOpen, setDesktopOpen] = useState(false);
  const [mobileLangOpen, setMobileLangOpen] = useState(false);
  const desktopRef = useRef(null);

  useEffect(() => {
    if (!isMobile) {
      const handler = (e) => {
        if (desktopRef.current && !desktopRef.current.contains(e.target)) {
          setDesktopOpen(false);
        }
      };
      document.addEventListener("mousedown", handler);
      return () => document.removeEventListener("mousedown", handler);
    }
  }, [isMobile]);

  if (!isMobile) {
    return (
      <div ref={desktopRef} style={{ position: "relative" }}>
        <button
          onClick={() => setDesktopOpen(p => !p)}
          style={{
            display: "flex", alignItems: "center", gap: "8px",
            background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.3)",
            borderRadius: "12px", padding: "0.5rem 0.75rem", color: "white",
            fontSize: "0.9rem", cursor: "pointer"
          }}
        >
          <span style={{ fontSize: "1.4em" }}>{currentLang.flag}</span>
          <span>{currentLang.name}</span>
          <Chevron animate={{ rotate: desktopOpen ? 225 : 45 }} />
        </button>

        <AnimatePresence>
          {desktopOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            style={{
              position: "absolute", top: "100%", right: 0, marginTop: "8px",
              background: "rgba(20,20,30,0.95)", backdropFilter: "blur(20px)",
              border: "1px solid rgba(255,255,255,0.15)", borderRadius: "14px",
              minWidth: "180px", overflow: "hidden",
              boxShadow: "0 10px 30px rgba(0,0,0,0.5)", zIndex: 1000
            }}
          >
            {languages.map(lang => (
              <button
                key={lang.code}
                onClick={() => { i18n.changeLanguage(lang.code); setDesktopOpen(false); }}
                style={{
                  width: "100%", padding: "0.75rem 1rem", background: "none", border: "none",
                  color: "white", textAlign: "left", display: "flex", alignItems: "center", gap: "12px",
                  fontSize: "0.95rem", cursor: "pointer"
                }}
                onMouseEnter={e => e.target.style.background = "rgba(255,255,255,0.1)"}
                onMouseLeave={e => e.target.style.background = "none"}
              >
                <span style={{ fontSize: "1.4em" }}>{lang.flag}</span> {lang.name}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    );
  }

  // Мобильная версия
  return (
    <>
      <motion.div
        variants={itemVariants}
        onClick={() => setMobileLangOpen(p => !p)}
        style={{
          fontSize: "1.4rem",
          fontWeight: 500,
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0.5rem 0"
        }}
      >
        <span>{currentLang.flag} {currentLang.name}</span>
        <Chevron animate={{ rotate: mobileLangOpen ? 225 : 45 }} />
      </motion.div>

      <AnimatePresence>
        {mobileLangOpen && (
          <motion.div
            initial="closed"
            animate="open"
            exit="closed"
            variants={{ open: { transition: { staggerChildren: 0.08 } } }}
            style={{ paddingLeft: "1rem", marginTop: "0.5rem" }}
          >
            {languages.map(lang => (
              <motion.div
                key={lang.code}
                variants={langItemVariants}
                onClick={() => {
                  i18n.changeLanguage(lang.code);
                  setMobileLangOpen(false);
                  onCloseMenu?.();
                }}
                style={{
                  padding: "0.6rem 0",
                  cursor: "pointer",
                  fontSize: "1.25rem",
                  opacity: lang.code === i18n.resolvedLanguage ? 1 : 0.7,
                  fontWeight: lang.code === currentLang ? 600 : 400
                }}
              >
                {lang.flag} {lang.name}
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// ======================= HEADER =======================
const Header = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => { window.scrollTo(0, 0); }, [location]);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const closeMenu = () => setOpen(false);

  const navItems = [
    { to: "/", label: t("nav_home") },
    { to: "/about", label: t("nav_about") },
    { to: "/features", label: t("nav_features") },
    { to: "/team", label: t("nav_team") },
    { to: "/pricing", label: t("nav_pricing") },
    { to: "/contactus", label: t("nav_contact") }
  ];

  return (
    <>
      <GlobalAnimations />
      <Nav scrolled={scrolled}>
        <Logo to="/">Plano AI</Logo>

        <NavLinks>
          {navItems.map(item => (
            <Link key={item.to} to={item.to}>{item.label}</Link>
          ))}
          <LanguageSelector />
          <DownloadButton href="https://plano.ai" target="_blank" rel="noopener">
            {t("nav_download")}
          </DownloadButton>
        </NavLinks>

        <Burger open={open} onClick={() => setOpen(!open)}>
          <span /><span /><span />
        </Burger>
      </Nav>

      <AnimatePresence>
        {open && (
          <MobileMenu
            variants={menuContainerVariants}
            initial="closed"
            animate="open"
            exit="closed"
          >
            {navItems.map(item => (
              <motion.div key={item.to} variants={itemVariants}>
                <Link onClick={closeMenu} to={item.to} style={{ fontSize: "1.4rem", fontWeight: 500 }}>
                  {item.label}
                </Link>
              </motion.div>
            ))}

            <motion.div variants={itemVariants}>
              <LanguageSelector isMobile onCloseMenu={closeMenu} />
            </motion.div>

            <motion.div variants={itemVariants}>
              <DownloadButton href="https://plano.ai" target="_blank" rel="noopener">
                {t("nav_download")}
              </DownloadButton>
            </motion.div>
          </MobileMenu>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;